import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from '@pwc/appkit-react';

import { defaultSideBarWithColumnsToolPanel } from '../../shared/displayComponents/agGrid/constants';
import AgGrid from '../../shared/displayComponents/agGrid/agGrid.component';
import { useRowEditMode } from '../../shared/editMode';
import useModal from '../../shared/hooks/useModal.hook';
import { columnBlueprintHeaderOptions } from '../../shared/columnDefinitions/constants';
import columnBlueprintStyles from '../../shared/styles/columnBlueprintStyles.module.scss';
import useUrlParams from '../../sharedSubPages/returnWorkspace/hooks/useUrlParams.hook';
import {
  taxYearSelector,
  consolidationsOptionsSelector,
  periodSelector,
  jurisdictionIdSelector,
  isFetchingJurisdictionsSelector,
  jurisdictionStateCodeSelector,
} from '../store/selectors';
import { EverywhereJurisdictionDropdownOption } from '../../shared/constants';

import { calcSpecTypes } from './constants';
import ValidationErrorModal from './validationErrorModal.component';
import validateCalcSpecs from './validateCalcSpecs';
import getColumnBlueprintBasedColumnDefinitions from './calcSpecs.columnDefinition';
import {
  fetchConsolCalcSpecs,
  updateConsolCalcSpecs,
  createConsolCalcSpecs,
  blockContextChanges,
} from './store/actions';
import {
  consolCalcSpecsSelector,
  consolCalcSpecsColumnsBlueprintSelector,
  isFetchingConsolCalcSpecsSelector,
  isFetchingCalcSpecsOptionsSelector,
  accountOptionsSelector,
  categoryOptionsSelector,
  groupedFilingAttributesSelector,
  isUpdatingConsolCalcSpecsSelector,
  isCreatingConsolCalcSpecsSelector,
} from './store/selectors';

const getFormattedValues = row => ({
  ...row,
  multiplier: parseFloat(row.multiplier),
  displayOrder: Number(row.displayOrder),
  calcPreferences: Number(row.calcPreferences),
  accountJurisdictionId: String(row.accountJurisdictionId),
});

const getUniqueRowId = ({ data: { rowId } }) => rowId;

const fillCells = params => params.initialValues[0];

const ConsolCalcSpecs = ({ categories = [], hasUserPermissionsToEdit }) => {
  const { queryParams, setParams } = useUrlParams();
  const [validationResult, setValidationResult] = useState({});
  const [addedRowsCounter, setAddedRowsCounter] = useState(0);

  const dispatch = useDispatch();

  const taxYear = useSelector(taxYearSelector);
  const period = useSelector(periodSelector);
  const jurisdictionId = useSelector(jurisdictionIdSelector);
  const isFetchingJurisdictions = useSelector(isFetchingJurisdictionsSelector);
  const jurisdictionStateCode = useSelector(jurisdictionStateCodeSelector);

  const calcSpecs = useSelector(consolCalcSpecsSelector);
  const calcSpecsColumnsBlueprint = useSelector(consolCalcSpecsColumnsBlueprintSelector);
  const isFetchingCalcSpecs = useSelector(isFetchingConsolCalcSpecsSelector);

  const accountOptions = useSelector(accountOptionsSelector);
  const categoryOptions = useSelector(categoryOptionsSelector);
  const consolidationOptions = useSelector(consolidationsOptionsSelector);
  const groupedFilingAttributes = useSelector(groupedFilingAttributesSelector);
  const isFetchingCalcSpecsOptions = useSelector(isFetchingCalcSpecsOptionsSelector);

  const isUpdatingCalcSpecs = useSelector(isUpdatingConsolCalcSpecsSelector);
  const isCreatingCalcSpecs = useSelector(isCreatingConsolCalcSpecsSelector);

  const isContextReady = taxYear && jurisdictionId;

  useEffect(() => {
    if (queryParams.search || queryParams.search === '') {
      setParams({ queryParams: { search: null } });
    }
  }, [setParams, queryParams.search]);

  const { showModal: showValidationErrorModal, modalProps } = useModal();

  const calcSpecInitialValues = useMemo(
    () => ({
      consolId: jurisdictionStateCode,
      categoryId: '',
      subcategory: '',
      accountId: '',
      accountJurisdictionId: '',
      multiplier: '1',
      displayOrder: '100',
      calcPreferences: '0',
    }),
    [jurisdictionStateCode],
  );

  const accountJurisdictionOptions = useMemo(
    () => [{ value: jurisdictionId, label: jurisdictionId }, EverywhereJurisdictionDropdownOption],
    [jurisdictionId],
  );

  const displayNameAndSelectOptionDictionary = useMemo(
    () => ({
      accountId: accountOptions,
      categoryId: categoryOptions,
      consolId: [
        { label: jurisdictionStateCode, value: jurisdictionStateCode },
        ...consolidationOptions,
      ],
      accountJurisdictionId: accountJurisdictionOptions,
    }),
    [
      accountOptions,
      categoryOptions,
      consolidationOptions,
      accountJurisdictionOptions,
      jurisdictionStateCode,
    ],
  );

  useEffect(() => {
    if (isContextReady) {
      dispatch(fetchConsolCalcSpecs({ taxYear, jurisdictionId, period }));
    }
  }, [dispatch, isContextReady, taxYear, jurisdictionId, period]);

  const isLoading =
    !isContextReady ||
    isFetchingCalcSpecs ||
    isFetchingCalcSpecsOptions ||
    isUpdatingCalcSpecs ||
    isCreatingCalcSpecs ||
    isFetchingJurisdictions;

  const onSaveCalcSpec = useCallback(
    async ({ rowsPairsWithChanges, rowsToAdd }) => {
      const formattedModifiedRows = rowsPairsWithChanges.map(({ newRow }) => {
        const formattedValues = getFormattedValues(newRow);

        return { ...newRow, ...formattedValues };
      });

      const formattedAddedRows = rowsToAdd.map(newRow => {
        const formattedValues = getFormattedValues(newRow);

        return {
          ...newRow,
          ...formattedValues,
          taxYear: Number(taxYear),
          jurisdictionId,
          period: Number(period),
        };
      });

      const { duplicatedRowsIds, rowsWithInvalidCells, modalMessage } = validateCalcSpecs({
        addOrModifiedRows: [...formattedModifiedRows, ...formattedAddedRows],
        calcSpecs,
        displayNameAndSelectOptionDictionary,
        calcSpecType: calcSpecTypes.CONSOL,
        groupedFilingAttributes,
      });

      if (modalMessage) {
        showValidationErrorModal(modalMessage);
        setValidationResult({ duplicatedRowsIds, rowsWithInvalidCells });
        return { shouldCancelHookStateReset: true };
      }

      if (rowsPairsWithChanges.length) {
        await dispatch(
          updateConsolCalcSpecs({
            values: formattedModifiedRows,
            taxYear,
            period,
            jurisdictionId,
          }),
        );
      }

      if (rowsToAdd.length) {
        await dispatch(
          createConsolCalcSpecs({
            values: formattedAddedRows,
            taxYear,
            period,
            jurisdictionId,
          }),
        );
      }

      setAddedRowsCounter(0);
      setValidationResult({});
      dispatch(blockContextChanges(false));
      dispatch(fetchConsolCalcSpecs({ taxYear, jurisdictionId, period }));
    },
    [
      showValidationErrorModal,
      setValidationResult,
      setAddedRowsCounter,
      dispatch,
      jurisdictionId,
      taxYear,
      period,
      calcSpecs,
      displayNameAndSelectOptionDictionary,
      groupedFilingAttributes,
    ],
  );

  const onEnterEdit = useCallback(() => {
    dispatch(blockContextChanges(true));
  }, [dispatch]);

  const onCancelEdit = useCallback(() => {
    setValidationResult({});
    dispatch(blockContextChanges(false));
  }, [setValidationResult, dispatch]);

  const filteredRowData = useMemo(() => {
    const selectedCategoriesSet = new Set(categories);
    return calcSpecs.filter(({ categoryId }) => selectedCategoriesSet.has(categoryId));
  }, [calcSpecs, categories]);

  const {
    navigationPrompt,
    isInEditMode,
    setIsInEditMode,
    editModeButtons,
    clonedRowData,
    updateRow,
    addRow,
    deleteRow,
    onGridReady,
  } = useRowEditMode({
    onEnter: onEnterEdit,
    onSave: onSaveCalcSpec,
    onCancel: onCancelEdit,
    rowData: filteredRowData,
    getUniqueRowId,
    editButtonDisabled: isLoading || !filteredRowData,
    appendRowUnderSelectedRow: true,
  });

  const addCalcSpec = useCallback(() => {
    addRow({ ...calcSpecInitialValues, isAddedRow: true, rowId: String(addedRowsCounter) });
    setAddedRowsCounter(addedRowsCounter + 1);
    setIsInEditMode(true);
    dispatch(blockContextChanges(true));
  }, [
    addRow,
    setAddedRowsCounter,
    setIsInEditMode,
    dispatch,
    addedRowsCounter,
    calcSpecInitialValues,
  ]);

  const columnDefinitions = useMemo(
    () =>
      getColumnBlueprintBasedColumnDefinitions({
        updateRow,
        onDeleteIconClick: deleteRow,
        isInEditMode,
        columnsBlueprint: calcSpecsColumnsBlueprint,
        displayNameAndSelectOptionDictionary,
        validationResult,
      }),
    [
      updateRow,
      deleteRow,
      isInEditMode,
      calcSpecsColumnsBlueprint,
      displayNameAndSelectOptionDictionary,
      validationResult,
    ],
  );

  return (
    <>
      {navigationPrompt}
      <ValidationErrorModal {...modalProps} />
      <div className="add-button-column">
        {hasUserPermissionsToEdit && (
          <>
            <Button size="lg" className="add-button" onClick={addCalcSpec} disabled={isLoading}>
              Add Calc Spec
            </Button>
            {editModeButtons}
          </>
        )}
      </div>
      <div className={`row grid-row ${columnBlueprintStyles.gridContainer}`}>
        <div className="col">
          <AgGrid
            rowData={categories.length ? clonedRowData : []}
            columnDefs={columnDefinitions}
            isGridLoading={isLoading}
            onGridReady={onGridReady}
            initialQuickFilterValue={queryParams.search || ''}
            withSearchBar
            enableRangeSelection
            suppressCopyRowsToClipboard
            suppressMultiRangeSelection
            enableFillHandle={isInEditMode}
            fillOperation={fillCells}
            undoRedoCellEditing
            stopEditingWhenCellsLoseFocus
            sideBar={defaultSideBarWithColumnsToolPanel}
            {...columnBlueprintHeaderOptions}
            enableBrowserTooltips
            areHeaderCellBordersEnabled
          />
        </div>
      </div>
    </>
  );
};

ConsolCalcSpecs.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string.isRequired),
  hasUserPermissionsToEdit: PropTypes.bool.isRequired,
};

export default ConsolCalcSpecs;
